import { useState, useEffect } from 'react';
import { FaTimes, FaUser, FaCheck } from 'react-icons/fa';

interface AttendeeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  users: any[];
  selectedUsers: string[];
  maxUsers: number;
  onConfirm: (selectedUsers: string[]) => void;
}

export const AttendeeSelectionModal = ({
  isOpen,
  onClose,
  title,
  users,
  selectedUsers,
  maxUsers,
  onConfirm
}: AttendeeSelectionModalProps) => {
  const [localSelection, setLocalSelection] = useState<string[]>(selectedUsers);

  useEffect(() => {
    setLocalSelection(selectedUsers);
  }, [selectedUsers, isOpen]);

  if (!isOpen) return null;

  const handleUserToggle = (userId: string) => {
    setLocalSelection(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else if (prev.length < maxUsers) {
        return [...prev, userId];
      }
      return prev;
    });
  };

  const handleConfirm = () => {
    onConfirm(localSelection);
    onClose();
  };

  const getUserName = (user: any) => {
    return `${user.user?.first_name || user.first_name} ${user.user?.last_name || user.last_name}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Select up to {maxUsers} attendees ({localSelection.length}/{maxUsers} selected)
            </p>
          </div>

          <div className="space-y-2">
            {users.map((user) => {
              const userId = user.id || user.user_id;
              const isSelected = localSelection.includes(userId);
              const isDisabled = !isSelected && localSelection.length >= maxUsers;
              
              return (
                <div
                  key={userId}
                  onClick={() => !isDisabled && handleUserToggle(userId)}
                  className={`flex items-center p-3 rounded-lg border transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-blue-50 border-blue-200 ring-1 ring-blue-500'
                      : isDisabled
                      ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-60'
                      : 'bg-white border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    {/* Avatar */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-blue-500' : 'bg-gray-200'
                    }`}>
                      {isSelected ? (
                        <FaCheck className="w-3 h-3 text-white" />
                      ) : (
                        <FaUser className={`w-3 h-3 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
                      )}
                    </div>

                    {/* User Info */}
                    <div className="flex-1">
                      <div className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                        {getUserName(user)}
                      </div>
                      {user.user?.email && (
                        <div className="text-xs text-gray-500">{user.user.email}</div>
                      )}
                    </div>

                    {/* Checkbox */}
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected
                        ? 'bg-blue-500 border-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {isSelected && <FaCheck className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {users.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <FaUser className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No users available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {localSelection.length} of {maxUsers} selected
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Confirm Selection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 